export const useFullscreenService = () => {
  // Proctive 데스크톱 앱(webview) 안에서는 전체화면을 앱이 네이티브 창 단위로 소유한다.
  // 여기서 HTML5 Fullscreen까지 요청하면 두 개의 전체화면 상태가 생겨 서로 어긋나고,
  // fullscreen 엘리먼트가 top layer로 승격되면서 앱의 커스텀 타이틀바를 가린다.
  // 따라서 webview 환경에서는 요청/해제를 건너뛰고 "이미 전체화면"으로 취급한다.
  const isProctiveWebview = (): boolean =>
    (window as any).codepresso?.isCodepressoWebView ?? false;

  const getFullscreenElement = (): Element | null =>
    document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? null;

  const requestFullscreen = (el: Element = document.documentElement): Promise<void> => {
    if (isProctiveWebview()) return Promise.resolve();
    const request = el.requestFullscreen?.bind(el)
      ?? (el as any).webkitRequestFullscreen?.bind(el);
    if (!request) return Promise.reject(new Error('Fullscreen not supported'));
    const result = request();
    return result instanceof Promise ? result : Promise.resolve();
  };

  const exitFullscreen = async (): Promise<void> => {
    if (isProctiveWebview()) return;
    if (!getFullscreenElement()) return;
    if (document.exitFullscreen) await document.exitFullscreen();
    else if ((document as any).webkitExitFullscreen) (document as any).webkitExitFullscreen();
  };

  // webview에서는 앱이 창을 전체화면으로 유지하므로 항상 true.
  // 이 값 하나로 ensureFullscreen()이 자동으로 no-op이 된다.
  const isFullscreen = (): boolean =>
    isProctiveWebview() || getFullscreenElement() !== null;

  const ensureFullscreen = async (el?: Element): Promise<void> => {
    if (isFullscreen()) return;
    await requestFullscreen(el).catch(() => {});
  };

  return { requestFullscreen, exitFullscreen, isFullscreen, ensureFullscreen };
};
